import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { usersApi } from '../../../api/usersApi';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: 600,
    borderRadius: '20px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.18)',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
  },
}));

const steps = [
  'Informations personnelles',
  'Fonction et accès',
  'Identifiants',
  'Récapitulatif'
];

const CreateUserDialog = ({ open, onClose, onUserCreated }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    functions: '',
    access_level: '',
    id_service: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    setError('');
  };

  const validateStep = () => {
    switch (activeStep) {
      case 0:
        return formData.firstname.trim() && formData.lastname.trim();
      case 1:
        return formData.functions.trim() && formData.access_level !== '' && formData.id_service !== '';
      case 2:
        return formData.email.trim() && formData.password.trim() && formData.password === formData.confirmPassword;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const accessToken = localStorage.getItem('accessToken');
      const userData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        functions: formData.functions,
        access_level: parseInt(formData.access_level),
        id_service: parseInt(formData.id_service),
        email: formData.email,
        password: formData.password
      };

      const response = await usersApi.createUser(userData, accessToken);
      
      if (response.status === 200) {
        onUserCreated(response.data);
        handleClose();
      } else {
        setError(response.message || 'Erreur lors de la création de l\'utilisateur');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur lors de la création de l\'utilisateur');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setActiveStep(0);
              setFormData({
          firstname: '',
          lastname: '',
          functions: '',
          access_level: '',
          id_service: '',
          email: '',
          password: '',
          confirmPassword: ''
        });
      setError('');
      onClose();
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prénom"
                value={formData.firstname}
                onChange={handleChange('firstname')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom"
                value={formData.lastname}
                onChange={handleChange('lastname')}
                required
                margin="normal"
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Fonction"
                value={formData.functions}
                onChange={handleChange('functions')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Niveau d'accès</InputLabel>
                <Select
                  value={formData.access_level}
                  onChange={handleChange('access_level')}
                  label="Niveau d'accès"
                >
                  <MenuItem value={0}>Fort (0)</MenuItem>
                  <MenuItem value={1}>Faible (1)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Service</InputLabel>
                <Select
                  value={formData.id_service}
                  onChange={handleChange('id_service')}
                  label="Service"
                >
                  <MenuItem value={0}>Administrateur (0)</MenuItem>
                  <MenuItem value={3}>Commercial (3)</MenuItem>
                  <MenuItem value={4}>Relance (4)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mot de passe"
                type="password"
                value={formData.password}
                onChange={handleChange('password')}
                required
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirmer le mot de passe"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange('confirmPassword')}
                required
                margin="normal"
                error={formData.password !== formData.confirmPassword && formData.confirmPassword !== ''}
                helperText={formData.password !== formData.confirmPassword && formData.confirmPassword !== '' ? 'Les mots de passe ne correspondent pas' : ''}
              />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>Récapitulatif</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Prénom</Typography>
                <Typography variant="body1">{formData.firstname}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Nom</Typography>
                <Typography variant="body1">{formData.lastname}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Fonction</Typography>
                <Typography variant="body1">{formData.functions}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Niveau d'accès</Typography>
                <Typography variant="body1">
                  {formData.access_level === '0' ? 'Fort (0)' : 'Faible (1)'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Service</Typography>
                <Typography variant="body1">
                  {formData.id_service === '0' ? 'Administrateur (0)' : 
                   formData.id_service === '3' ? 'Commercial (3)' : 'Relance (4)'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                <Typography variant="body1">{formData.email}</Typography>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1.25rem' }}>
        Créer un utilisateur
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {renderStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            backgroundColor: 'rgba(100,116,139,0.07)',
            color: '#475569',
            border: '1px solid rgba(100,116,139,0.18)',
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            px: 2.5
          }}
        >
          Annuler
        </Button>
        <Button
          disabled={activeStep === 0 || loading}
          onClick={handleBack}
          sx={{
            backgroundColor: 'rgba(100,116,139,0.07)',
            color: '#475569',
            border: '1px solid rgba(100,116,139,0.18)',
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            px: 2.5
          }}
        >
          Retour
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!validateStep() || loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#fff',
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
            px: 3,
            '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)' },
            '&.Mui-disabled': { background: '#cbd5e1', color: '#fff' }
          }}
        >
          {activeStep === steps.length - 1 ? 'Créer' : 'Suivant'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

CreateUserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUserCreated: PropTypes.func.isRequired,
};

export default CreateUserDialog; 